import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  Input,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import React, { useState } from 'react';
import { Timestamp, collection, collectionGroup, deleteDoc, doc, getDocs, query, setDoc, where } from 'firebase/firestore';

import Papa from 'papaparse';
import { Voter } from '../../types';
import { createIndex } from '../../utils/indexer';
import { db } from '../../firebase';
import { useAuth } from '../../hooks/useAuth';

interface VoterData {
  name: string;
  precinct_number: string;
  barangay: string;
  city_municipality: string;
  district: string;
  province: string;
  region: string;
}

// Arrow function to load precinct voters from Firestore
const loadPrecinctVoters = async (
  precinctNumber: string, 
  barangay: string, 
  cityMunicipality: string, 
  province: string
): Promise<(Voter & { docId: string })[]> => {
  try {
    const precinctRef = collection(db, 'voters');
    
    // Build query with mandatory conditions
    const queryConditions = [
      where('precinct', '==', precinctNumber),
      where('barangay', '==', barangay),
      where('city_municipality', '==', cityMunicipality),
      where('province', '==', province)
    ];
    
    const q = query(precinctRef, ...queryConditions);
    
    const querySnapshot = await getDocs(q);
    
    const voters: (Voter & { docId: string })[] = [];
    querySnapshot.forEach((document) => {
      const voterData = document.data() as Voter;
      voters.push({
        ...voterData,
        docId: document.id
      });
    });
    
    return voters;
  } catch (error) {
    console.error('Error loading precinct voters:', error);
    throw error;
  }
};

interface DeletedVoter {
  name: string;
  documentPath: string;
  deletedKeywordPaths?: string[];
}

interface AddedVoter {
  name: string;
  documentPath: string;
}

// Type guard to ensure type safety
function isValidDeletedVoter(voter: any): voter is DeletedVoter {
  return voter !== null && 
         typeof voter.name === 'string' && 
         typeof voter.documentPath === 'string' &&
         (voter.deletedKeywordPaths === undefined || Array.isArray(voter.deletedKeywordPaths));
}

const ResolvePrecintVoters: React.FC = () => {
  const { user } = useAuth();
  const [csvFiles, setCsvFiles] = useState<File[]>([]);
  const [parsedVotersList, setParsedVotersList] = useState<VoterData[][]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [deletedVotersList, setDeletedVotersList] = useState<DeletedVoter[][]>([]);
  const [addedVotersList, setAddedVotersList] = useState<AddedVoter[][]>([]);
  const [progress, setProgress] = useState(0);
  const [isResolving, setIsResolving] = useState(false);
  const [currentFileIndex, setCurrentFileIndex] = useState(0);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files ? Array.from(event.target.files) : [];
    if (files.length > 0) {
      // Reset all states
      setCsvFiles(files);
      setErrorMessage(null);
      setDeletedVotersList([]);
      setAddedVotersList([]);
      setIsResolving(false);
      setCurrentFileIndex(0);
      
      // Parse all CSV files
      const parsedFilePromises = files.map(file => 
        new Promise<VoterData[]>((resolve, reject) => {
          Papa.parse<VoterData>(file, {
            header: true,
            complete: (results) => {
              // Validate CSV headers
              const expectedHeaders = [
                'name', 'precinct_number', 'barangay', 
                'city_municipality', 'district', 'province', 'region'
              ];
              
              const actualHeaders = Object.keys(results.data[0] || {});
              const headersMatch = expectedHeaders.every(header => 
                actualHeaders.includes(header)
              );

              if (headersMatch) {
                resolve(results.data.filter(row => 
                  Object.values(row).some(val => val !== '')
                ));
              } else {
                reject(new Error(`Invalid CSV file (${file.name}). Please check the headers.`));
              }
            },
            error: (error) => {
              console.error('CSV Parsing Error:', error);
              reject(new Error(`Error parsing CSV file (${file.name}).`));
            }
          });
        })
      );

      Promise.all(parsedFilePromises)
        .then(parsedVotersResults => {
          setParsedVotersList(parsedVotersResults);
        })
        .catch(error => {
          setErrorMessage(error.message);
          setCsvFiles([]);
          setParsedVotersList([]);
        });
    }
  };

  const handleResolveVoters = async () => {
    if (parsedVotersList.length > 0) {
      // Reset states
      setIsResolving(true);
      setProgress(0);
      setDeletedVotersList([]);
      setAddedVotersList([]);
      setCurrentFileIndex(0);

      // Process files sequentially
      await processNextFile(0);
    }
  };

  const processNextFile = async (fileIndex: number) => {
    const parsedVoters = parsedVotersList[fileIndex];

    // Check if all voters belong to the same precinct
    const precinctNumbers = new Set(parsedVoters.map(voter => voter.precinct_number));
    
    if (precinctNumbers.size > 1) {
      // Multiple precincts detected
      setErrorMessage(`Error in file ${csvFiles[fileIndex].name}: Voters belong to different precincts. Please upload voters from a single precinct.`);
      setIsResolving(false);
      return;
    }
    
    const precinctNumber = Array.from(precinctNumbers)[0];
    
    // Get the first voter's details for additional filters
    const firstVoter = parsedVoters[0];
    
    try {
      const firestoreVoters = await loadPrecinctVoters(
        precinctNumber, 
        firstVoter.barangay, 
        firstVoter.city_municipality, 
        firstVoter.province
      );
      
      // Process cleanup and additions
      await cleanupVoters(firestoreVoters, fileIndex);
      
      // Process next file or finish
      const nextFileIndex = fileIndex + 1;
      if (nextFileIndex < parsedVotersList.length) {
        setCurrentFileIndex(nextFileIndex);
        await processNextFile(nextFileIndex);
      } else {
        // All files processed
        setIsResolving(false);
        setProgress(0);
      }
    } catch (error) {
      setErrorMessage(prev => 
        (prev || '') + 
        `\nError resolving precinct voters in file ${csvFiles[fileIndex].name}`
      );
      console.error(error);
      
      // Continue with next file or finish
      const nextFileIndex = fileIndex + 1;
      if (nextFileIndex < parsedVotersList.length) {
        setCurrentFileIndex(nextFileIndex);
        await processNextFile(nextFileIndex);
      } else {
        setIsResolving(false);
        setProgress(0);
      }
    }

    async function cleanupVoters(firestoreVoters: (Voter & { docId: string; })[], fileIndex: number) {
      const parsedVoters = parsedVotersList[fileIndex];
      const firestoreVoterNames = new Set(firestoreVoters.map(voter => voter.name));
      const parsedVoterNames = new Set(parsedVoters.map(voter => voter.name));

      // Find and delete voters not in the parsed list
      const votersToDelete = firestoreVoters.filter(
        voter => !parsedVoterNames.has(voter.name)
      );

      // Find and add voters missing from Firestore
      const votersToAdd = parsedVoters.filter(
        voter => !firestoreVoterNames.has(voter.name)
      );

      // Delete voters from Firestore
      const deletePromises = votersToDelete.map(async (voter, index) => {
        try {
          const voterRef = doc(db, 'voters', voter.docId);
          const voterDocumentPath = `voters/${voter.docId}`;

          // Find and delete related keyword entries
          const keywordQuery = query(
            collectionGroup(db, 'keywordEntries'),
            where('document', '==', 'voter'),
            where('field', '==', 'name'),
            where('value', '==', voter.name)
          );

          const keywordSnapshot = await getDocs(keywordQuery);
          const keywordDeletePromises = keywordSnapshot.docs.map(async (keywordDoc) => {
            await deleteDoc(keywordDoc.ref);
            return keywordDoc.ref.path;
          });

          const deletedKeywordPaths = await Promise.all(keywordDeletePromises);

          // Delete the voter document
          await deleteDoc(voterRef);

          // Update progress for this file
          const fileProgress = ((index + 1) / votersToDelete.length) * 100;
          const overallProgress = ((fileIndex + fileProgress / 100) / parsedVotersList.length) * 100;
          setProgress(overallProgress);

          return {
            name: voter.name,
            documentPath: voterDocumentPath,
            deletedKeywordPaths: deletedKeywordPaths
          };
        } catch (deleteError) {
          console.error(`Error deleting voter ${voter.name}:`, deleteError);
          return null;
        }
      });

      // Add new voters to Firestore
      const addPromises = votersToAdd.map(async (voter, index) => {
        try {
          const voterToAdd: Voter = {
            id: '', // Will be replaced by Firestore's auto-generated ID
            name: voter.name,
            precinct: voter.precinct_number,
            barangay: voter.barangay,
            city_municipality: voter.city_municipality,
            district: voter.district,
            province: voter.province,
            region: voter.region,
            organizations: [], // Empty organizations list
            updatedAt: Timestamp.now(),
            updatedBy: user?.uid ?? 'system'
          };

          // Add voter to Firestore
          const votersRef = collection(db, "voters");
          const voterDocRef = doc(votersRef);
          await setDoc(voterDocRef, {
            ...voterToAdd,
            id: voterDocRef.id,
          });          
          
          // Create index for the new voter's name
          await createIndex('voter', 'name', voterToAdd.name, `voters/${voterDocRef.id}`, voterToAdd.updatedBy ?? 'system');

          // Update progress for this file
          const fileProgress = ((index + 1) / votersToAdd.length) * 100;
          const overallProgress = ((fileIndex + fileProgress / 100) / parsedVotersList.length) * 100;
          setProgress(overallProgress);

          return {
            name: voter.name,
            documentPath: `voters/${voterDocRef.id}`
          };
        } catch (addError) {
          console.error(`Error adding voter ${voter.name}:`, addError);
          return null;
        }
      });

      const deletedVoterDetails = await Promise.all(deletePromises);
      const successfullyDeletedVoters = deletedVoterDetails.filter(isValidDeletedVoter);

      const addedVoterDetails = await Promise.all(addPromises);
      const successfullyAddedVoters = addedVoterDetails.filter(
        (voter): voter is AddedVoter => voter !== null && voter.name !== undefined
      );

      // Update deleted voters list
      setDeletedVotersList(prev => {
        const updatedList = [...prev];
        updatedList[fileIndex] = successfullyDeletedVoters.map(voter => ({
          name: voter!.name,
          documentPath: voter!.documentPath,
          ...(voter!.deletedKeywordPaths && { deletedKeywordPaths: voter!.deletedKeywordPaths })
        }));
        return updatedList;
      });

      // Update added voters list
      setAddedVotersList(prev => {
        const updatedList = [...prev];
        updatedList[fileIndex] = successfullyAddedVoters;
        return updatedList;
      });

      // Update error message
      setErrorMessage(prev => (prev ?? '') +
        `Resolved Precinct ${precinctNumber} (${csvFiles[fileIndex].name}). ` +
        `Total Firestore Voters: ${firestoreVoters.length}. ` +
        `Deleted Voters: ${successfullyDeletedVoters.length}. ` +
        `Added Voters: ${successfullyAddedVoters.length}\n\n`
      );

      console.log(`Deleted Voters for ${csvFiles[fileIndex].name}:`, successfullyDeletedVoters);
      console.log(`Added Voters for ${csvFiles[fileIndex].name}:`, successfullyAddedVoters);
    }
  };

  // Rest of the component remains the same as in the previous implementation
  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Resolve Precinct Voters
      </Typography>
      <Paper elevation={3} sx={{ p: 3, mt: 2 }}>
        {errorMessage && (
          <Grid item xs={12} sx={{ mb: 2 }}>
            <Alert 
              severity={errorMessage.startsWith('Resolved') ? 'success' : 'error'}
              onClose={() => setErrorMessage(null)}
            >
              {errorMessage}
            </Alert>
          </Grid>
        )}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Input
                type="file"
                inputProps={{ 
                  accept: '.csv',
                  multiple: true 
                }}
                onChange={handleFileUpload}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleResolveVoters}
              disabled={parsedVotersList.length === 0 || isResolving}
              fullWidth
            >
              Resolve Voters
            </Button>
          </Grid>
          {isResolving && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress} 
                sx={{ width: '100%' }} 
              />
              <Typography variant="body2" color="textSecondary" align="center">
                {`${Math.round(progress)}%`}
              </Typography>
              <Typography variant="body2" color="textSecondary" align="center">
                Processing: {csvFiles[currentFileIndex]?.name}
              </Typography>
            </Grid>
          )}
          {csvFiles.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="body2">
                Uploaded Files: {csvFiles.map(f => f.name).join(', ')} | 
                Total Voters: {parsedVotersList.reduce((sum, list) => sum + list.length, 0)}
              </Typography>
            </Grid>
          )}
          {(deletedVotersList.length > 0 || addedVotersList.length > 0) && (
            <Grid item xs={12} sx={{ mt: 2 }}>
              {deletedVotersList.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Deleted Voters
                  </Typography>
                  {deletedVotersList.map((deletedVoters, fileIndex) => (
                    <div key={fileIndex}>
                      <Typography variant="subtitle1">
                        {csvFiles[fileIndex].name}
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Document Path</TableCell>
                              {deletedVoters.some(v => v?.deletedKeywordPaths?.length) && (
                                <TableCell>Keyword Paths</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {deletedVoters.map((voter, index) => {
                              if (!voter || !voter.name || !voter.documentPath) return null;
                              
                              return (
                                <TableRow key={index}>
                                  <TableCell>{voter.name}</TableCell>
                                  <TableCell>{voter.documentPath}</TableCell>
                                  {voter.deletedKeywordPaths?.length && (
                                    <TableCell>
                                      {voter.deletedKeywordPaths.join(', ')}
                                    </TableCell>
                                  )}
                                </TableRow>
                              );
                            }).filter(Boolean)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  ))}
                </>
              )}
              {addedVotersList.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom>
                    Added Voters
                  </Typography>
                  {addedVotersList.map((addedVoters, fileIndex) => (
                    <div key={fileIndex}>
                      <Typography variant="subtitle1">
                        {csvFiles[fileIndex].name}
                      </Typography>
                      <TableContainer>
                        <Table>
                          <TableHead>
                            <TableRow>
                              <TableCell>Name</TableCell>
                              <TableCell>Document Path</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {addedVoters.map((voter, index) => {
                              if (!voter || !voter.name || !voter.documentPath) return null;
                              
                              return (
                                <TableRow key={index}>
                                  <TableCell>{voter.name}</TableCell>
                                  <TableCell>{voter.documentPath}</TableCell>
                                </TableRow>
                              );
                            }).filter(Boolean)}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </div>
                  ))}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default ResolvePrecintVoters;
